/**
 * **A table of the firm's own words** — the `comparison` section's editor
 * (C21, Wave 21G, T-4.90, ADR-0043 §2).
 *
 * The schema says it as two lists: `columns` (2–4 labels) and `rows` (up to
 * fifteen, each a label and a list of `cells`, one per column). Drawn as two
 * lists that is a form in which cell three of row nine has to be matched to
 * column three by counting. So the two lists are drawn together, as a grid:
 * the columns as a list of labels, and each row as its label and one box per
 * column, each box named by its column.
 *
 * **The column count is the grid's.** A row saved with fewer cells than there
 * are columns — the service keeps one, as a row still being written (21A's
 * note §4.6) — is **padded empty on load**; a row with more is cut to the
 * columns, which is all the service accepts. Adding a column adds an empty
 * cell to every row, and removing one removes that column's cell from every
 * row, so a cell never slides under the wrong heading.
 *
 * Every limit is the schema's (`min_items`, `max_items`, `max_length`), read
 * here and never restated. **Nothing validates**: the service does.
 */

import { Button, Input } from '@fiveox/ui'
import { Plus, Trash2 } from 'lucide-react'

import type { FieldSchema } from '../../../lib/api/site'

type Row = Record<string, unknown>

/** Which fields make up a grid — a lookup, as `MEASURED_IMAGES` is, so a
 *  second table-shaped section is one line. */
export interface CellGrid {
  columns: string
  rows: string
  cells: string
  /** The one text field inside a column. */
  columnLabel: string
  /** The one text field inside a cell. */
  cellText: string
}

export const CELL_GRIDS: Record<string, CellGrid> = {
  comparison: { columns: 'columns', rows: 'rows', cells: 'cells', columnLabel: 'label', cellText: 'text' },
}

function asRows(value: unknown): Row[] {
  return Array.isArray(value) ? value.filter((r): r is Row => Boolean(r) && typeof r === 'object') : []
}

/**
 * Each row with exactly `count` cells — padded with empty ones, or cut. The
 * grid's rule on load and on every write.
 */
export function padCells(rows: unknown, count: number, cells = 'cells'): Row[] {
  return asRows(rows).map((row) => {
    const have = asRows(row[cells]).slice(0, count)
    while (have.length < count) have.push({})
    return { ...row, [cells]: have }
  })
}

const text = (value: unknown) => (typeof value === 'string' ? value : '')

export function CellGridField({
  grid,
  columnsField,
  rowsField,
  value,
  error,
  onChange,
}: {
  grid: CellGrid
  columnsField: FieldSchema
  rowsField: FieldSchema
  /** The whole section's props — the grid writes two of them at once. */
  value: Row
  error: string | null
  onChange: (patch: Row) => void
}) {
  const columns = asRows(value[grid.columns])
  const rows = padCells(value[grid.rows], columns.length, grid.cells)

  const columnMin = columnsField.min_items ?? 0
  const columnMax = columnsField.max_items ?? 4
  const rowMax = rowsField.max_items ?? 15
  const rowLabelField = rowsField.of?.find((f) => f.name === 'label')
  const columnLabelField = columnsField.of?.find((f) => f.name === grid.columnLabel)
  const cellField = rowsField.of?.find((f) => f.name === grid.cells)?.of?.find((f) => f.name === grid.cellText)

  const write = (nextColumns: Row[], nextRows: Row[]) =>
    onChange({
      [grid.columns]: nextColumns,
      [grid.rows]: padCells(nextRows, nextColumns.length, grid.cells),
    })

  const setColumn = (index: number, label: string) =>
    write(
      columns.map((c, i) => (i === index ? { ...c, [grid.columnLabel]: label } : c)),
      rows,
    )
  const addColumn = () => write([...columns, {}], rows)
  const removeColumn = (index: number) =>
    write(
      columns.filter((_, i) => i !== index),
      rows.map((row) => ({ ...row, [grid.cells]: asRows(row[grid.cells]).filter((_, i) => i !== index) })),
    )

  const setRow = (index: number, next: Row) => write(columns, rows.map((r, i) => (i === index ? next : r)))
  const setCell = (rowIndex: number, cellIndex: number, cellText: string) => {
    const row = rows[rowIndex] as Row
    const cells = asRows(row[grid.cells]).map((c, i) =>
      i === cellIndex ? { ...c, [grid.cellText]: cellText } : c,
    )
    setRow(rowIndex, { ...row, [grid.cells]: cells })
  }

  const columnName = (i: number) => text(columns[i]?.[grid.columnLabel]).trim() || `Column ${i + 1}`

  return (
    <div className="space-y-4" data-cell-grid="">
      {error && <p className="text-xs text-destructive">{error}</p>}

      {/* ── the columns ─────────────────────────────────────────────── */}
      <div>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-foreground">{columnsField.label}</span>
          <span className="text-xs text-muted-foreground">
            {columns.length} of {columnMax}
            {columnMin > 0 ? ` · at least ${columnMin}` : ''}
          </span>
        </div>
        <div className="space-y-2">
          {columns.map((column, i) => (
            <div key={i} className="flex items-center gap-2" data-grid-column={i}>
              <Input
                aria-label={`Column ${i + 1} label`}
                value={text(column[grid.columnLabel])}
                maxLength={columnLabelField?.max_length}
                onChange={(e) => setColumn(i, e.target.value)}
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove column ${i + 1}`}
                disabled={columns.length <= columnMin}
                onClick={() => removeColumn(i)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="mt-2"
          disabled={columns.length >= columnMax}
          onClick={addColumn}
        >
          <Plus size={14} /> Add a column
        </Button>
      </div>

      {/* ── the rows, one box per column ────────────────────────────── */}
      <div>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-foreground">{rowsField.label}</span>
          <span className="text-xs text-muted-foreground">
            {rows.length} of {rowMax}
          </span>
        </div>
        <div className="space-y-2">
          {rows.map((row, r) => (
            <div key={r} className="rounded-lg border border-border bg-surface-raised p-3" data-grid-row={r}>
              <div className="mb-2 flex items-center gap-2">
                <Input
                  aria-label={`Row ${r + 1} label`}
                  placeholder={rowLabelField?.label}
                  value={text(row.label)}
                  maxLength={rowLabelField?.max_length}
                  onChange={(e) => setRow(r, { ...row, label: e.target.value })}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove row ${r + 1}`}
                  onClick={() => write(columns, rows.filter((_, i) => i !== r))}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {asRows(row[grid.cells]).map((cell, c) => (
                  <label key={c} className="block space-y-1">
                    <span className="block text-xs text-muted-foreground">{columnName(c)}</span>
                    <Input
                      aria-label={`Row ${r + 1}, ${columnName(c)}`}
                      value={text(cell[grid.cellText])}
                      maxLength={cellField?.max_length}
                      onChange={(e) => setCell(r, c, e.target.value)}
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="mt-2"
          disabled={rows.length >= rowMax || columns.length === 0}
          onClick={() => write(columns, [...rows, {}])}
        >
          <Plus size={14} /> Add a row
        </Button>
      </div>
    </div>
  )
}
